// src/components/customers/DestinationsManager.js - Gestione destinazioni cliente
import React, { useState } from 'react';
import { Plus, Trash2, MapPin, Building } from 'lucide-react';

const DestinationsManager = ({ destinations = [], onChange }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDestination, setNewDestination] = useState({
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    contactPerson: '',
    phone: '',
    email: '',
    notes: '',
    isDefault: false,
    isActive: true
  });

  const handleAddDestination = () => {
    if (!newDestination.name.trim() || !newDestination.address.trim()) {
      return;
    }

    const destination = {
      name: newDestination.name.trim(),
      address: newDestination.address.trim(),
      city: newDestination.city.trim(),
      province: newDestination.province.trim().toUpperCase(),
      postalCode: newDestination.postalCode.trim(),
      contactPerson: newDestination.contactPerson.trim(),
      phone: newDestination.phone.trim(),
      email: newDestination.email.trim().toLowerCase(),
      notes: newDestination.notes.trim(),
      isDefault: newDestination.isDefault,
      isActive: newDestination.isActive
    };

    onChange([...destinations, destination]);
    setNewDestination({
      name: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
      contactPerson: '',
      phone: '',
      email: '',
      notes: '',
      isDefault: false,
      isActive: true
    });
    setShowAddForm(false);
  };

  const handleRemoveDestination = (index) => {
    onChange(destinations.filter((_, i) => i !== index));
  };

  const handleEditDestination = (index, field, value) => {
    const updated = destinations.map((dest, i) =>
      i === index ? { ...dest, [field]: value } : dest
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {/* Lista destinazioni esistenti */}
      {destinations.map((destination, index) => (
        <div key={index} className={`rounded-lg p-4 border-2 ${destination.isDefault ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium text-gray-900">{destination.name}</h4>
              {destination.isDefault && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  Predefinita
                </span>
              )}
              {!destination.isActive && (
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                  Inattiva
                </span>
              )}
            </div>
            <button
              onClick={() => handleRemoveDestination(index)}
              className="p-1 text-red-500 hover:text-red-700 transition-colors"
              title="Rimuovi destinazione"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Nome Destinazione *
              </label>
              <input
                type="text"
                value={destination.name || ''}
                onChange={(e) => handleEditDestination(index, 'name', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Es. Magazzino Nord"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Indirizzo *
              </label>
              <input
                type="text"
                value={destination.address || ''}
                onChange={(e) => handleEditDestination(index, 'address', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Via/Piazza..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Città
              </label>
              <input
                type="text"
                value={destination.city || ''}
                onChange={(e) => handleEditDestination(index, 'city', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Milano"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Provincia
              </label>
              <input
                type="text"
                value={destination.province || ''}
                onChange={(e) => handleEditDestination(index, 'province', e.target.value.toUpperCase())}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="MI"
                maxLength="2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                CAP
              </label>
              <input
                type="text"
                value={destination.postalCode || ''}
                onChange={(e) => handleEditDestination(index, 'postalCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="20100"
                maxLength="5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Persona di Riferimento
              </label>
              <input
                type="text"
                value={destination.contactPerson || ''}
                onChange={(e) => handleEditDestination(index, 'contactPerson', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Mario Rossi"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Telefono
              </label>
              <input
                type="tel"
                value={destination.phone || ''}
                onChange={(e) => handleEditDestination(index, 'phone', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="+39 02 1234567"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="email"
                value={destination.email || ''}
                onChange={(e) => handleEditDestination(index, 'email', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="magazzino@azienda.it"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Note per la Consegna
              </label>
              <textarea
                value={destination.notes || ''}
                onChange={(e) => handleEditDestination(index, 'notes', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Note per il corriere..."
                rows="2"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mt-3 pt-3 border-t border-gray-200">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={destination.isDefault || false}
                onChange={(e) => handleEditDestination(index, 'isDefault', e.target.checked)}
                className="text-blue-600"
              />
              <span className="text-xs text-gray-600">Destinazione predefinita</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={destination.isActive !== false}
                onChange={(e) => handleEditDestination(index, 'isActive', e.target.checked)}
                className="text-green-600"
              />
              <span className="text-xs text-gray-600">Attiva</span>
            </label>
          </div>
        </div>
      ))}

      {/* Form aggiunta nuova destinazione */}
      {showAddForm ? (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
            <MapPin className="w-4 h-4 mr-2" />
            Nuova Destinazione
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Nome Destinazione *
              </label>
              <input
                type="text"
                value={newDestination.name}
                onChange={(e) => setNewDestination(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Es. Magazzino Nord"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Indirizzo *
              </label>
              <input
                type="text"
                value={newDestination.address}
                onChange={(e) => setNewDestination(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Via/Piazza..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Città
              </label>
              <input
                type="text"
                value={newDestination.city}
                onChange={(e) => setNewDestination(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Milano"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Provincia
              </label>
              <input
                type="text"
                value={newDestination.province}
                onChange={(e) => setNewDestination(prev => ({ ...prev, province: e.target.value.toUpperCase() }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="MI"
                maxLength="2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                CAP
              </label>
              <input
                type="text"
                value={newDestination.postalCode}
                onChange={(e) => setNewDestination(prev => ({ ...prev, postalCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="20100"
                maxLength="5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Persona di Riferimento
              </label>
              <input
                type="text"
                value={newDestination.contactPerson}
                onChange={(e) => setNewDestination(prev => ({ ...prev, contactPerson: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Mario Rossi"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Telefono
              </label>
              <input
                type="tel"
                value={newDestination.phone}
                onChange={(e) => setNewDestination(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="+39 02 1234567"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="email"
                value={newDestination.email}
                onChange={(e) => setNewDestination(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="magazzino@azienda.it"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Note per la Consegna
              </label>
              <textarea
                value={newDestination.notes}
                onChange={(e) => setNewDestination(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Note per il corriere..."
                rows="2"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mb-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={newDestination.isDefault}
                onChange={(e) => setNewDestination(prev => ({ ...prev, isDefault: e.target.checked }))}
                className="text-blue-600"
              />
              <span className="text-xs text-gray-600">Destinazione predefinita</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={newDestination.isActive}
                onChange={(e) => setNewDestination(prev => ({ ...prev, isActive: e.target.checked }))}
                className="text-green-600"
              />
              <span className="text-xs text-gray-600">Attiva</span>
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleAddDestination}
              disabled={!newDestination.name.trim() || !newDestination.address.trim()}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aggiungi
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewDestination({
                  name: '',
                  address: '',
                  city: '',
                  province: '',
                  postalCode: '',
                  contactPerson: '',
                  phone: '',
                  email: '',
                  notes: '',
                  isDefault: false,
                  isActive: true
                });
              }}
              className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
            >
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Aggiungi Destinazione</span>
        </button>
      )}

      {destinations.length === 0 && !showAddForm && (
        <p className="text-xs text-gray-500 text-center py-2">
          Nessuna destinazione aggiunta. Le destinazioni permettono di specificare indirizzi di consegna alternativi.
        </p>
      )}
    </div>
  );
};

export default DestinationsManager;